import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { Done as FinishIcon } from '@mui/icons-material';
import type { Appointment } from '@app-types/reception';

interface FinishVisitModalProps {
  open: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  onConfirm: (appointmentId: string) => Promise<void>;
}

export const FinishVisitModal: React.FC<FinishVisitModalProps> = ({
  open,
  appointment,
  onClose,
  onConfirm
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!appointment) return;

    setLoading(true);
    setError(null);

    try {
      await onConfirm(appointment.id);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Eroare la finalizarea vizitei');
    } finally {
      setLoading(false);
    }
  };

  if (!appointment) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <FinishIcon color="success" />
          Finalizare vizita
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body1" gutterBottom>
          Confirmati finalizarea vizitei pentru:
        </Typography>

        <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, mt: 2 }}>
          <Typography variant="body2">
            <strong>Animal:</strong> {appointment.animal?.name || 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Proprietar:</strong>{' '}
            {appointment.owner
              ? `${appointment.owner.firstName} ${appointment.owner.lastName}`
              : 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Medic:</strong>{' '}
            {appointment.doctor
              ? `Dr. ${appointment.doctor.firstName} ${appointment.doctor.lastName}`
              : 'N/A'}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          Vizita va fi marcata ca finalizata si timpii vor fi inregistrati.
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Anuleaza
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="success"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <FinishIcon />}
        >
          {loading ? 'Se proceseaza...' : 'Finalizeaza vizita'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

